/**
 * EvolutionRow — una métrica a lo largo del tiempo: nombre · línea · hoy · delta.
 *
 * Implementa el **Evolution Pattern** de DESIGN_SYSTEM.md. Leer ahí el porqué;
 * acá está el cómo. Las reglas del pattern que viven en este archivo y no en
 * quien lo usa, a propósito:
 *
 *   · Con menos de dos mediciones NO dibuja: dice que falta historia. Está acá
 *     para que ninguna pantalla pueda olvidarlo — un gráfico de un punto es una
 *     recta que sugiere que no pasó nada.
 *   · El delta va contra la PRIMERA medición, no contra la anterior.
 *   · `mejorEsMas` decide el color, no el signo del número. En hallazgos de SEO
 *     y en posición de Google bajar es mejorar.
 *
 * SVG a mano y sin librería: son cuarenta líneas y no hay un segundo gráfico en
 * la aplicación que justifique una dependencia.
 */
import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Pill } from '../PillGroup.js';

export interface Medicion {
  /** ISO. En orden cronológico: la primera es la línea de base. */
  fecha: string;
  valor: number;
}

interface EvolutionRowProps {
  label: string;
  mediciones: Medicion[];
  /** Si subir es mejorar. Falso para hallazgos, errores o posición en Google. */
  mejorEsMas: boolean;
  /** Cómo se escribe el valor. Por defecto, el número tal cual. */
  formato?: (v: number) => string;
  /** La última de un grupo no lleva divisor. */
  last?: boolean;
}

/** Alto del gráfico. Una fila de lista, para que cuatro entren sin scroll. */
const ALTO = 34;
/** Radio del punto. También el margen interno: sin él, el primero y el último se cortan. */
const R = 2.5;
/** Ancho reservado al nombre, para que las cuatro líneas arranquen alineadas. */
const ETIQUETA_W = 132;

/**
 * ¿Este delta es una mejora?
 *
 * Función aparte y exportada porque es la regla que se lee al revés si se
 * escribe mal, y desde afuera no se nota: un `-3` en hallazgos de SEO es una
 * mejora y tiene que ir en cyan. Un empate cuenta como no-empeoró.
 */
export function esMejora(delta: number, mejorEsMas: boolean): boolean {
  return mejorEsMas ? delta >= 0 : delta <= 0;
}

/** Cuántas mediciones hacen falta para que la línea diga algo. Regla 1 del pattern. */
export const MINIMO_MEDICIONES = 2;

const fila: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--evo-space-3)',
  padding: 'var(--evo-space-3) 0',
};

const textoEtiqueta: CSSProperties = {
  flex: `0 0 ${String(ETIQUETA_W)}px`,
  fontSize: 'var(--evo-fs-label)',
  color: 'var(--evo-text-muted)',
  fontFamily: 'var(--evo-font)',
};

/**
 * El ancho real del contenedor.
 *
 * Hace falta medirlo: un viewBox estirado con `preserveAspectRatio="none"`
 * deformaría los puntos en óvalos y el grosor de la línea con él.
 */
function useAncho(): [RefObject<HTMLDivElement>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [ancho, setAncho] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new ResizeObserver(([e]) => { setAncho(e?.contentRect.width ?? 0); });
    obs.observe(el);
    return () => { obs.disconnect(); };
  }, []);
  return [ref, ancho];
}

export function EvolutionRow({ label, mediciones, mejorEsMas, formato, last = false }: EvolutionRowProps) {
  const { i18n } = useTranslation();
  const [ref, ancho] = useAncho();

  const escribir = formato ?? ((v: number) => String(v));
  const hayHistoria = mediciones.length >= MINIMO_MEDICIONES;

  const primero = mediciones[0];
  const ultimo = mediciones[mediciones.length - 1];
  const previo = mediciones[mediciones.length - 2];
  const delta = primero && ultimo ? ultimo.valor - primero.valor : 0;
  // El color lo decide la métrica. Un delta de −3 en hallazgos de SEO es una
  // mejora, y pintarlo de rojo por el signo es enseñar a leerlo al revés.
  const mejoro = esMejora(delta, mejorEsMas);
  const color = mejoro ? 'var(--evo-cyan)' : 'var(--evo-danger)';
  // La pastilla es un AVISO, no un resumen: aparece solo cuando algo cambió
  // desde la última medición, y en "menos es mejor" solo cuando apareció un
  // problema. Eduardo (2026-09-06): "−7 es una estupidez, debe quedar en 0; si
  // aparece un problema se marca". El color de la línea ya dice si va bien.
  const cambio = previo && ultimo ? ultimo.valor - previo.valor : 0;
  const aviso = cambio !== 0 && (mejorEsMas || cambio > 0);
  const avisoMejora = esMejora(cambio, mejorEsMas);

  return (
    <div style={{ ...fila, borderBottom: last ? 'none' : '1px solid var(--evo-stroke)' }}>
      <span style={textoEtiqueta}>{label}</span>

      <div ref={ref} style={{ flex: 1, minWidth: 0, height: ALTO }}>
        {hayHistoria && ancho > 0
          ? <Linea mediciones={mediciones} ancho={ancho} color={color} idioma={i18n.language} escribir={escribir} />
          : null}
      </div>

      {hayHistoria && ultimo
        ? (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--evo-space-2)', flex: '0 0 auto' }}>
            <span style={{ fontSize: 'var(--evo-list-title-fs)', color: 'var(--evo-fg-1)', fontFamily: 'var(--evo-font)' }}>
              {escribir(ultimo.valor)}
            </span>
            {aviso && (
              <Pill tone={avisoMejora ? 'cyan' : 'danger'} variant="subtle" size="xs">
                {cambio > 0 ? `+${escribir(cambio)}` : escribir(cambio)}
              </Pill>
            )}
          </div>
        )
        : <SinHistoria />}
    </div>
  );
}

/** Lo que se ve cuando hay una sola medición, o ninguna. */
function SinHistoria() {
  const { t } = useTranslation();
  return (
    <span style={{ fontSize: 'var(--evo-fs-tiny)', color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)' }}>
      {t('common.evolution.noHistory')}
    </span>
  );
}

interface LineaProps {
  mediciones: Medicion[];
  ancho: number;
  color: string;
  idioma: string;
  escribir: (v: number) => string;
}

export interface Punto { cx: number; cy: number; m: Medicion }

/**
 * Dónde va cada punto y qué camino los une, en píxeles del contenedor medido.
 *
 * Pura y exportada para poder probarla: el error que este cálculo comete es
 * silencioso —una línea que se dibuja igual con el eje invertido— y a ojo se ve
 * como un gráfico normal que cuenta lo contrario.
 */
export function geometria(mediciones: Medicion[], ancho: number): { puntos: Punto[]; trazo: string; area: string } {
  const valores = mediciones.map(m => m.valor);
  const min = Math.min(...valores);
  const max = Math.max(...valores);
  const plano = max === min;

  const x = (i: number) => R + (i / (mediciones.length - 1)) * (ancho - 2 * R);
  // Más alto es más arriba: en SVG la y crece hacia abajo, así que el valor
  // grande tiene que dar la y chica. Invertir esto dibuja la caída como subida.
  // Sin variación no hay curva: una recta al medio dice lo que pasó mejor que
  // una pegada al piso, que se lee como "está en cero".
  const y = (v: number) => plano
    ? ALTO / 2
    : ALTO - R - ((v - min) / (max - min)) * (ALTO - 2 * R);

  const puntos = mediciones.map((m, i) => ({ cx: x(i), cy: y(m.valor), m }));
  const trazo = puntos.map((p, i) => `${i === 0 ? 'M' : 'L'}${String(p.cx)},${String(p.cy)}`).join(' ');
  const area = `${trazo} L${String(x(mediciones.length - 1))},${String(ALTO)} L${String(x(0))},${String(ALTO)} Z`;
  return { puntos, trazo, area };
}

function Linea({ mediciones, ancho, color, idioma, escribir }: LineaProps) {
  const { puntos, trazo, area } = geometria(mediciones, ancho);

  const fecha = (iso: string) =>
    new Date(iso).toLocaleDateString(idioma, { day: 'numeric', month: 'short', year: '2-digit' });

  return (
    <svg width={ancho} height={ALTO} style={{ display: 'block', overflow: 'visible' }} aria-hidden>
      <path d={area} fill={color} opacity={0.12} />
      <path d={trazo} fill="none" stroke={color} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
      {puntos.map(p => (
        // El dato exacto al pasar por encima, sin ejes ni grilla. `<title>` nativo:
        // no hace falta estado ni un tooltip propio para mostrar dos datos.
        <circle key={p.m.fecha} cx={p.cx} cy={p.cy} r={R} fill={color}>
          <title>{`${fecha(p.m.fecha)} · ${escribir(p.m.valor)}`}</title>
        </circle>
      ))}
    </svg>
  );
}
